import { getCookie, setCookie, deleteCookie } from '../utils/cookie-utils.js';

/**
 * Storage manager for B2M Tracker
 * Supports localStorage with in-memory fallback for 3rd-party iframe contexts
 * where localStorage may be blocked by browser privacy policies (Safari ITP, Firefox ETP, etc.)
 */
export default class Storage {
  constructor() {
    this.keys = {
      bulkEventQueue: "b2m_bulk_event_queue",
      customEventQueue: "b2m_custom_event_queue",
      userId: "b2m_user_id",
      deviceRecord: "b2m_device",
      firstTimestamp: "b2m_first_timestamp",
      sessionId: "b2m_session_id",
      sessionStartTime: "b2m_session_start_time"
    };

    // Cross-subdomain sharing. When the SDK sets this to a parent domain
    // (e.g. ".birfatura.com"), the keys below are mirrored to a cookie scoped to
    // that domain so sibling subdomains inherit campaign attribution.
    // PERSON identity and session keys are deliberately excluded — different
    // applications can live on sibling subdomains (e.g. a dashboard on
    // analytics.<domain> and the marketing site on www.<domain>), and sharing
    // identity leaked one app's logged-in user id into the other's events and
    // experiment assignments. b2m_external_user_id in particular stays host-only.
    //
    // The DEVICE record (b2m_device) is shared, and must be: since identity
    // became a random per-install salt instead of a deterministic hash of device
    // signals, a fresh subdomain has no way to re-derive it and would count the
    // same visitor twice. The record holds a device-instance id and hashed
    // hardware signals — it says nothing about who is logged in, so it does not
    // reintroduce the leak that host-only identity keys exist to prevent.
    // null → host-only, no sharing.
    this.cookieDomain = null;
    this._crossDomainKeys = new Set([
      'b2m_initial_utm',
      'b2m_campaign_params',
      'b2m_referrer',
      'b2m_initial_referrer',
      'b2m_device',
      // Acquisition timestamp for the 32-day attribution send-window. Kept
      // long-lived (NOT an attribution key) so the window check still works
      // after the 32-day utm cookies themselves have expired.
      'b2m_attribution_ts'
    ]);
    // Identity/session cookies written to the parent domain by older SDK
    // versions. Deleted on init so browsers that already carry a leaked
    // identity (e.g. a dashboard login mirrored to .domain) self-heal.
    this._legacyCrossDomainKeys = [
      this.keys.userId,
      this.keys.firstTimestamp,
      this.keys.sessionId,
      this.keys.sessionStartTime,
      'b2m_external_user_id'
    ];

    // In-memory fallback store for when localStorage is unavailable
    this._memoryStore = {};
    // Cache the localStorage availability check result
    this._storageAvailable = null;
    // Cache the sessionStorage availability check result
    this._sessionStorageAvailable = null;

    // Migrate: clear any session keys left in localStorage from before the
    // sessionStorage migration so old stale data doesn't linger.
    this._migrateSession();
  }

  _migrateSession() {
    try {
      localStorage.removeItem(this.keys.sessionId);
      localStorage.removeItem(this.keys.sessionStartTime);
    } catch (e) { /* ignore */ }
  }

  // ---- Cross-subdomain cookie mirroring ----------------------------------
  // Only the allow-listed keys above are mirrored, and only when cookieDomain
  // is configured. Session keys use a session cookie (no expiry) so that
  // closing the browser still starts a new session (GA-like behaviour).

  _cookieShareEnabled(key) {
    return !!this.cookieDomain && this._crossDomainKeys.has(key);
  }

  _isSessionKey(key) {
    return key === this.keys.sessionId || key === this.keys.sessionStartTime;
  }

  _isAttributionKey(key) {
    return key === 'b2m_initial_utm' || key === 'b2m_campaign_params' ||
           key === 'b2m_referrer' || key === 'b2m_initial_referrer';
  }

  _writeCookie(key, value) {
    // Session keys → session cookie (cleared on browser close).
    // Attribution (UTM/referrer) → 32-day window: source stops being attributed
    // after 32 days. Identity (user_id, first_timestamp) → long-lived (365d).
    let days;
    if (this._isSessionKey(key)) days = null;
    else if (this._isAttributionKey(key)) days = 32;
    else days = 365;
    setCookie(key, JSON.stringify(value), days, this.cookieDomain);
  }

  _readCookie(key) {
    const raw = getCookie(key);
    if (raw == null) return undefined;
    try { return JSON.parse(raw); } catch (e) { return undefined; }
  }

  /**
   * Cheap guard: if our parent-domain cookies have been deleted by an external
   * script/CMP (a common cause of cookies "appearing then vanishing") but we
   * still hold the values in localStorage, repopulate them. Called before every
   * send so cross-subdomain attribution survives external cookie reaping.
   * Sentinel: only re-mirror when we HOLD attribution locally but its cookie
   * is gone — avoids rewriting on every call.
   */
  refreshCookiesIfMissing() {
    if (!this.cookieDomain) return;

    // The device record has its own sentinel: a visitor with no attribution at
    // all still needs it re-mirrored, otherwise sibling subdomains lose the
    // shared device identity and start counting the visitor twice.
    const localRecord = this.get(this.keys.deviceRecord);
    if (localRecord && getCookie(this.keys.deviceRecord) == null) {
      this._writeCookie(this.keys.deviceRecord, localRecord);
    }

    const hasLocalAttribution =
      this.get('b2m_campaign_params') || this.get('b2m_initial_utm');
    if (!hasLocalAttribution) return;
    if (getCookie('b2m_campaign_params') == null && getCookie('b2m_initial_utm') == null) {
      this.reMirrorToCookies();
    }
  }

  /**
   * Delete identity/session cookies that pre-attribution-only SDK versions
   * wrote to the parent domain. Runs once per page load (init): heals browsers
   * where a sibling app's login (e.g. analytics.<domain>) leaked an external
   * user id onto every other subdomain.
   */
  cleanupLegacyCrossDomainCookies() {
    if (!this.cookieDomain) return;
    this._legacyCrossDomainKeys.forEach(key => {
      try { deleteCookie(key, this.cookieDomain); } catch (e) { /* ignore */ }
    });
  }

  /**
   * Re-write all cross-domain keys to cookies from their current values. Used
   * after consent is granted: a CMP may have blocked document.cookie before
   * consent, so values captured into localStorage/sessionStorage never reached
   * the parent-domain cookie. This persists them now so siblings can inherit.
   */
  reMirrorToCookies() {
    if (!this.cookieDomain) return;
    this._crossDomainKeys.forEach(key => {
      const val = this._isSessionKey(key) ? this._sessionGet(key) : this.get(key);
      if (val !== null && val !== undefined) this._writeCookie(key, val);
    });
  }
  
  /**
   * Check if sessionStorage is available.
   * sessionStorage is cleared when the browser tab/window is closed, so
   * session IDs stored here reset on every new browser session — matching
   * GA's session behaviour instead of persisting across browser restarts.
   * @returns {boolean}
   */
  _isSessionStorageAvailable() {
    if (this._sessionStorageAvailable !== null) return this._sessionStorageAvailable;
    try {
      const testKey = '__b2m_ss_test__';
      sessionStorage.setItem(testKey, '1');
      sessionStorage.removeItem(testKey);
      this._sessionStorageAvailable = true;
    } catch (e) {
      this._sessionStorageAvailable = false;
    }
    return this._sessionStorageAvailable;
  }

  _sessionGet(key) {
    let local;
    try {
      if (this._isSessionStorageAvailable()) {
        const v = sessionStorage.getItem(key);
        local = v ? JSON.parse(v) : null;
      } else {
        local = this._memoryStore.hasOwnProperty(key) ? this._memoryStore[key] : null;
      }
    } catch (e) {
      local = this._memoryStore.hasOwnProperty(key) ? this._memoryStore[key] : null;
    }
    if (local !== null && local !== undefined) return local;
    // Cross-domain fallback: a fresh subdomain has empty sessionStorage but the
    // parent-domain cookie still carries the active session — restore it so the
    // user keeps the same session_id instead of starting a new one.
    if (this._cookieShareEnabled(key)) {
      const fromCookie = this._readCookie(key);
      if (fromCookie !== undefined && fromCookie !== null) {
        this._sessionSet(key, fromCookie);
        return fromCookie;
      }
    }
    return null;
  }

  _sessionSet(key, value) {
    try {
      if (this._isSessionStorageAvailable()) {
        sessionStorage.setItem(key, JSON.stringify(value));
      } else {
        this._memoryStore[key] = value;
      }
    } catch (e) {
      this._memoryStore[key] = value;
    }
    if (this._cookieShareEnabled(key)) this._writeCookie(key, value);
  }

  _sessionRemove(key) {
    try {
      if (this._isSessionStorageAvailable()) {
        sessionStorage.removeItem(key);
      } else {
        delete this._memoryStore[key];
      }
    } catch (e) {
      delete this._memoryStore[key];
    }
    if (this._cookieShareEnabled(key)) deleteCookie(key, this.cookieDomain);
  }

  /**
   * Check if localStorage is available
   * In 3rd-party iframe contexts, localStorage may throw SecurityError
   * @returns {boolean}
   */
  _isLocalStorageAvailable() {
    if (this._storageAvailable !== null) return this._storageAvailable;
    
    try {
      const testKey = '__b2m_storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      this._storageAvailable = true;
    } catch (e) {
      console.warn('[B2M SDK] localStorage unavailable (3rd-party iframe context detected), using in-memory storage');
      this._storageAvailable = false;
    }
    return this._storageAvailable;
  }
  
  get(key, defaultValue = null) {
    let local = null;
    try {
      if (this._isLocalStorageAvailable()) {
        const value = localStorage.getItem(key);
        local = value ? JSON.parse(value) : null;
      } else {
        // In-memory fallback
        local = this._memoryStore.hasOwnProperty(key) ? this._memoryStore[key] : null;
      }
    } catch (e) {
      // If localStorage fails at runtime, fall back to memory
      local = this._memoryStore.hasOwnProperty(key) ? this._memoryStore[key] : null;
    }
    if (local !== null && local !== undefined) return local;
    // Cross-domain fallback: a fresh subdomain has empty localStorage but the
    // parent-domain cookie still holds the value — restore it locally and use it.
    if (this._cookieShareEnabled(key)) {
      const fromCookie = this._readCookie(key);
      if (fromCookie !== undefined && fromCookie !== null) {
        this.set(key, fromCookie); // rehydrate local (re-mirrors cookie, idempotent)
        return fromCookie;
      }
    }
    return defaultValue;
  }

  set(key, value) {
    try {
      if (this._isLocalStorageAvailable()) {
        localStorage.setItem(key, JSON.stringify(value));
      } else {
        // In-memory fallback
        this._memoryStore[key] = value;
      }
    } catch (e) {
      // If localStorage fails at runtime, fall back to memory
      this._memoryStore[key] = value;
    }
    // Mirror cross-domain keys to a parent-domain cookie so sibling subdomains
    // inherit attribution / identity.
    if (this._cookieShareEnabled(key)) this._writeCookie(key, value);
    return true;
  }

  remove(key) {
    try {
      if (this._isLocalStorageAvailable()) {
        localStorage.removeItem(key);
      } else {
        delete this._memoryStore[key];
      }
    } catch (e) {
      delete this._memoryStore[key];
    }
    if (this._cookieShareEnabled(key)) deleteCookie(key, this.cookieDomain);
  }
  
  /**
   * Check if storage is using in-memory fallback (i.e., localStorage is blocked)
   * @returns {boolean}
   */
  isUsingMemoryFallback() {
    return !this._isLocalStorageAvailable();
  }
  
  // User ID methods
  getUserId() {
    return this.get(this.keys.userId);
  }
  
  setUserId(id) {
    return this.set(this.keys.userId, id);
  }

  // Device record methods
  // The record pairs a device-instance id with the hashed component vector that
  // proves the id belongs to this device (see core/device-fingerprint.js).
  getDeviceRecord() {
    return this.get(this.keys.deviceRecord);
  }

  setDeviceRecord(record) {
    return this.set(this.keys.deviceRecord, record);
  }

  // First timestamp methods
  getFirstTimestamp() {
    let timestamp = this.get(this.keys.firstTimestamp);
    if (!timestamp) {
      timestamp = Math.floor(Date.now() / 1000);
      this.setFirstTimestamp(timestamp);
    }
    return timestamp;
  }
  
  setFirstTimestamp(timestamp) {
    return this.set(this.keys.firstTimestamp, timestamp);
  }
  
  // Bulk event queue methods
  getBulkQueue() {
    return this.get(this.keys.bulkEventQueue, []);
  }
  
  getBulkQueueSize() {
    return this.getBulkQueue().length;
  }
  
  addToBulkQueue(event) {
    const queue = this.getBulkQueue();
    queue.push(event);
    
    // Overflow protection: if localStorage write fails (quota exceeded),
    // drop the oldest events to make room
    const success = this.set(this.keys.bulkEventQueue, queue);
    if (!success && queue.length > 1) {
      // Drop oldest 25% of the queue and retry
      const trimCount = Math.max(1, Math.floor(queue.length * 0.25));
      const trimmed = queue.slice(trimCount);
      console.warn(`[B2M Storage] Queue overflow – dropped ${trimCount} oldest events`);
      return this.set(this.keys.bulkEventQueue, trimmed);
    }
    return success;
  }
  
  clearBulkQueue() {
    return this.set(this.keys.bulkEventQueue, []);
  }

  /**
   * Drop the first `count` events from the bulk queue — the batch that was
   * just transmitted. Events appended while that batch was in flight stay
   * queued; a blanket clearBulkQueue() here would silently lose them
   * (e.g. an ab_test_view tracked while the session_start batch was on the wire).
   */
  removeFromBulkQueue(count) {
    if (!count || count <= 0) return true;
    const queue = this.getBulkQueue();
    return this.set(this.keys.bulkEventQueue, queue.slice(count));
  }

  // Custom event queue methods
  getCustomQueue() {
    return this.get(this.keys.customEventQueue, []);
  }
  
  getCustomQueueSize() {
    return this.getCustomQueue().length;
  }
  
  addToCustomQueue(event) {
    const queue = this.getCustomQueue();
    queue.push(event);
    return this.set(this.keys.customEventQueue, queue);
  }
  
  clearCustomQueue() {
    return this.set(this.keys.customEventQueue, []);
  }

  /** Same batch-scoped removal as removeFromBulkQueue, for the custom queue. */
  removeFromCustomQueue(count) {
    if (!count || count <= 0) return true;
    const queue = this.getCustomQueue();
    return this.set(this.keys.customEventQueue, queue.slice(count));
  }
  
  // Session management methods
  // Session state is stored in sessionStorage so it resets when the browser
  // tab/window is closed — matching GA's "new browser session = new session" behaviour.
  getSessionId() {
    return this._sessionGet(this.keys.sessionId);
  }

  setSessionId(sessionId) {
    this._sessionSet(this.keys.sessionId, sessionId);
  }

  getSessionStartTime() {
    return this._sessionGet(this.keys.sessionStartTime);
  }

  setSessionStartTime(timestamp) {
    this._sessionSet(this.keys.sessionStartTime, timestamp);
  }

  clearSession() {
    this._sessionRemove(this.keys.sessionId);
    this._sessionRemove(this.keys.sessionStartTime);
  }
  
  isSessionExpired(maxAgeMs) {
    const startTime = this.getSessionStartTime();
    if (!startTime) return true;
    
    // Use provided timeout or default 30 minutes
    const timeout = maxAgeMs || 30 * 60 * 1000;
    return (Date.now() - startTime) > timeout;
  }

  // Generic storage methods
  setItem(key, value) {
    return this.set(key, value);
  }
  
  getItem(key, defaultValue = null) {
    return this.get(key, defaultValue);
  }

  // NOTE: a second remove() used to be declared here. Being later in the class
  // body it silently replaced the real one above, so removals skipped the
  // in-memory fallback and never deleted the parent-domain cookie — clearing an
  // external user id left it alive in storage. The real remove() is the only one.
  removeItem(key) {
    return this.remove(key);
  }
}